import React, { useState } from 'react';
import { ChevronDown, CircleX, TriangleAlert } from 'lucide-react';
import { BackupPreview } from '../../services/backupService';
import { Button, Dialog, DialogBody, DialogFooter, DialogHeader, Dot, EmptyState, IconButton, SectionHeader, Skeleton, StatGrid } from '../ui';

interface BackupPreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  preview: BackupPreview | null;
  loading?: boolean;
}

type PreviewEntity = BackupPreview['entities'][string];

const entityTone = (entity: PreviewEntity) => (entity.error ? 'red' : entity.dataLength > 0 ? 'green' : 'neutral');

const entityStatus = (entity: PreviewEntity) => {
  if (entity.error) return 'Error';
  if (entity.dataLength > 0) return entity.dataLength.toLocaleString();
  return 'Empty';
};

const samplePreview = (sample: unknown) => {
  const json = JSON.stringify(sample, null, 2);
  return json.length > 200 ? `${json.substring(0, 200)}…` : json;
};

export const BackupPreviewModal: React.FC<BackupPreviewModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  preview,
  loading = false,
}) => {
  const [openSample, setOpenSample] = useState<string | null>(null);

  const entities = preview ? Object.entries(preview.entities) : [];
  const withData = entities.filter(([, entity]) => entity.dataLength > 0).length;

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      labelledBy="backup-preview-title"
      handle
      className="max-h-[92vh] sm:max-h-[84vh] sm:max-w-2xl"
    >
      <DialogHeader
        id="backup-preview-title"
        eyebrow="Backup preview"
        title={<span className="break-all">{preview?.backupName ?? 'Backup'}</span>}
        onClose={onClose}
      />

      <DialogBody>
        {loading ? (
          <div className="space-y-2" aria-busy="true" aria-label="Loading preview">
            <Skeleton className="h-[76px] w-full rounded-2xl" />
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-9 w-full rounded-xl" />
            ))}
          </div>
        ) : preview ? (
          <>
            <StatGrid
              items={[
                { label: 'Records', value: preview.totalRecords.toLocaleString() },
                { label: 'With data', value: `${withData}/${entities.length}` },
                {
                  label: 'Errors',
                  value: <span className={preview.errors.length ? 'text-primary-700' : ''}>{preview.errors.length}</span>,
                },
                {
                  label: 'Warnings',
                  value: <span className={preview.warnings.length ? 'text-amber-700' : ''}>{preview.warnings.length}</span>,
                },
              ]}
            />

            {(preview.errors.length > 0 || preview.warnings.length > 0) && (
              <ul className="mt-4 space-y-1.5 text-[13px]">
                {preview.errors.map((message, index) => (
                  <li key={`e-${index}`} className="flex items-start gap-2 text-primary-700">
                    <CircleX className="mt-px h-4 w-4 flex-shrink-0" strokeWidth={1.75} />
                    <span className="break-words">{message}</span>
                  </li>
                ))}
                {preview.warnings.map((message, index) => (
                  <li key={`w-${index}`} className="flex items-start gap-2 text-amber-700">
                    <TriangleAlert className="mt-px h-4 w-4 flex-shrink-0" strokeWidth={1.75} />
                    <span className="break-words">{message}</span>
                  </li>
                ))}
              </ul>
            )}

            <SectionHeader title="Entities" aside={entities.length} className="mt-5" />
            <div className="divide-y divide-neutral-200/70 overflow-hidden rounded-2xl border border-neutral-200">
              {entities.map(([entityName, entity]) => {
                const hasSample = !!entity.sampleData && entity.sampleData.length > 0;
                const sampleOpen = openSample === entityName;
                return (
                  <div key={entityName} className="px-4 py-2">
                    <div className="flex min-h-[32px] items-center gap-3">
                      <Dot tone={entityTone(entity)} />
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-[13px] capitalize text-ink">{entityName.replace(/_/g, ' ')}</p>
                        {entity.lastModified && (
                          <p className="truncate text-[11.5px] text-neutral-400">
                            Modified {new Date(entity.lastModified).toLocaleDateString()}
                          </p>
                        )}
                      </div>
                      <span
                        className={`text-[13px] tabular-nums ${
                          entity.error ? 'text-primary-700' : entity.dataLength > 0 ? 'text-ink' : 'text-neutral-400'
                        }`}
                      >
                        {entityStatus(entity)}
                      </span>
                      {hasSample ? (
                        <IconButton
                          label={sampleOpen ? 'Hide sample' : 'Show sample'}
                          size="xs"
                          aria-expanded={sampleOpen}
                          onClick={() => setOpenSample(sampleOpen ? null : entityName)}
                        >
                          <ChevronDown
                            className={`h-4 w-4 transition-transform ${sampleOpen ? 'rotate-180' : ''}`}
                            strokeWidth={1.75}
                          />
                        </IconButton>
                      ) : (
                        <span className="w-7" aria-hidden="true" />
                      )}
                    </div>
                    {entity.error && (
                      <p className="mb-1 break-words pl-[18px] text-[12px] text-primary-700">{entity.error}</p>
                    )}
                    {hasSample && sampleOpen && (
                      <pre className="mb-1.5 ml-[18px] mt-1 animate-fade-up overflow-x-auto whitespace-pre-wrap break-all rounded-xl bg-neutral-50 p-3 font-ui-mono text-[11.5px] leading-relaxed text-neutral-600">
                        {samplePreview(entity.sampleData![0])}
                      </pre>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        ) : (
          <EmptyState title="No preview data" />
        )}
      </DialogBody>

      <DialogFooter>
        <Button variant="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="primary" onClick={onConfirm} disabled={loading || !preview}>
          Restore…
        </Button>
      </DialogFooter>
    </Dialog>
  );
};
